import { useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { asyncUploadService } from '../services/asyncUploadService';
import type { UploadNotification, ImportNotification, AuthIssueNotification } from '../types/notifications';
import { logInfo, logError } from '../services/logtail';
import { toast } from 'react-hot-toast';
import { handleStorageReauthNeeded } from '../services/storageService';
import { showUploadResultToast } from '../services/uploadNotificationToast';

function matchesFileTypeFilter(
  file_type: string,
  fileType?: string,
  fileTypes?: string[],
): boolean {
  if (fileTypes && fileTypes.length > 0) {
    return fileTypes.includes(file_type);
  }
  if (fileType) {
    return file_type === fileType;
  }
  return true;
}

interface UseWebSocketNotificationsOptions {
  trackId: string;
  fileType?: string; // Only process notifications for this file type
  fileTypes?: string[]; // Alternative: allow multiple file types (e.g. video + footages)
  showUploadToasts?: boolean; // Default true — deduped globally per upload_id
  onUploadComplete?: (notification: UploadNotification) => void;
  onUploadFailed?: (notification: UploadNotification) => void;
  onUploadUpdate?: (notification: UploadNotification) => void; // Called for progress updates
  onImportUpdate?: (notification: ImportNotification) => void;
  onAuthIssue?: (notification: AuthIssueNotification) => void;
  unlinkGoogleDrive?: () => void; // Add this for Google Drive state reset
}

export const useWebSocketNotifications = ({
  trackId,
  fileType,
  fileTypes,
  showUploadToasts = true,
  onUploadComplete,
  onUploadFailed,
  onUploadUpdate,
  onImportUpdate,
  onAuthIssue,
  unlinkGoogleDrive
}: UseWebSocketNotificationsOptions) => {
  const { user } = useAuth();
  const isRendering = useRef(false);
  
  // Use refs to always access latest callbacks without causing re-subscriptions
  const onUploadCompleteRef = useRef(onUploadComplete);
  const onUploadFailedRef = useRef(onUploadFailed);
  const onUploadUpdateRef = useRef(onUploadUpdate);
  const onImportUpdateRef = useRef(onImportUpdate);
  const onAuthIssueRef = useRef(onAuthIssue);
  const unlinkGoogleDriveRef = useRef(unlinkGoogleDrive);
  
  // Update refs when callbacks change
  useEffect(() => {
    onUploadCompleteRef.current = onUploadComplete;
    onUploadFailedRef.current = onUploadFailed;
    onUploadUpdateRef.current = onUploadUpdate;
    onImportUpdateRef.current = onImportUpdate;
    onAuthIssueRef.current = onAuthIssue;
    unlinkGoogleDriveRef.current = unlinkGoogleDrive;
  });

  // Set rendering flag to prevent state updates during render
  useEffect(() => {
    isRendering.current = true;
    const timeoutId = setTimeout(() => {
      isRendering.current = false;
    }, 0);
    return () => clearTimeout(timeoutId);
  });

  // Centralized upload notification handler
  const handleUploadNotification = useCallback((notification: UploadNotification) => {
    // Prevent state updates during render
    if (isRendering.current) {
      setTimeout(() => handleUploadNotification(notification), 0);
      return;
    }

    const { upload_id, status, file_id, error, filename, file_type } = notification;

    if (!matchesFileTypeFilter(file_type, fileType, fileTypes)) {
      return;
    }

    logInfo('Received upload notification', {
      notification,
      trackId,
      userId: user?.uid,
      filterFileType: fileType
    });

    // Handle progress updates (status === 'processing' with progress field)
    if (status === 'processing' && notification.progress !== undefined) {
      // Call progress update handler if provided
      if (onUploadUpdateRef.current) {
        onUploadUpdateRef.current(notification);
      }
    }

    if (status === 'completed' && file_id) {
      if (showUploadToasts) {
        showUploadResultToast(notification);
      }

      if (onUploadCompleteRef.current) {
        onUploadCompleteRef.current(notification);
      }

    } else if (status === 'failed') {
      // Check if this is an auth error
      const isAuthError = error && (
        error.toLowerCase().includes('invalid_grant') ||
        error.toLowerCase().includes('invalid_request') ||
        error.toLowerCase().includes('could not determine client id') ||
        error.toLowerCase().includes('oauth2') ||
        error.toLowerCase().includes('authentication failed') ||
        error.toLowerCase().includes('unauthorized') ||
        error.toLowerCase().includes('token expired') ||
        error.toLowerCase().includes('invalid token')
      );

      if (isAuthError) {
        logInfo('🔐 AUTH ISSUE: Detected in upload notification, prompting re-link', {
          trackId,
          userId: user?.uid,
          error
        });

        void handleStorageReauthNeeded({
          unlink: unlinkGoogleDriveRef.current,
          message: 'Your cloud connection expired. Reconnect to continue uploading.',
        });
      } else {
        // Regular upload failure
        logError('Upload failed', {
          error: error || 'Unknown error',
          uploadId: upload_id,
          trackId,
          userId: user?.uid,
          fileType: file_type,
          filename: filename,
          notification: notification
        });

        if (showUploadToasts) {
          showUploadResultToast(notification);
        }
      }

      // Call custom handler if provided
      if (onUploadFailedRef.current) {
        onUploadFailedRef.current(notification);
      }
    }
  }, [trackId, user?.uid, fileType, fileTypes, showUploadToasts]);

  // Centralized import notification handler
  const handleImportNotification = useCallback((notification: ImportNotification) => {
    // Prevent state updates during render
    if (isRendering.current) {
      setTimeout(() => handleImportNotification(notification), 0);
      return;
    }

    logInfo('Received import notification', { notification, trackId, userId: user?.uid });

    // Don't show toasts here - let the component handle them
    // Call custom handler if provided
    if (onImportUpdateRef.current) {
      onImportUpdateRef.current(notification);
    }
  }, [trackId, user?.uid]); // Using refs for callbacks now

  // Centralized auth issue handler
  const handleAuthIssue = useCallback((notification: AuthIssueNotification) => {
    const { service, status_code, requires_reauth } = notification;

    logInfo('🔐 AUTH ISSUE: Processing WebSocket notification', {
      service,
      status_code,
      requires_reauth,
      trackId,
      userId: user?.uid
    });

    const storageServices = new Set(['gdrive', 'dropbox', 'onedrive', 'yandex']);
    if (storageServices.has(service) && status_code === 401 && requires_reauth) {
      logInfo('🔐 AUTH ISSUE: Triggering cloud storage re-link flow', {
        service,
        trackId,
        userId: user?.uid
      });

      void handleStorageReauthNeeded({
        unlink: unlinkGoogleDriveRef.current,
        provider: service,
        reason: 'expired',
      });
    } else {
      // Fallback for other auth issues
      logInfo('🔔 TOAST: Showing generic auth issue notification', {
        service,
        trackId,
        source: 'useWebSocketNotifications-authIssueFallback',
        stackTrace: new Error().stack?.split('\n').slice(1, 4).join('\n')
      });
      toast.error('Google Drive authentication failed. Please re-authenticate with Google Drive.');
    }

    // Call custom handler if provided
    if (onAuthIssueRef.current) {
      onAuthIssueRef.current(notification);
    }
  }, [trackId, user?.uid]); // Removed onAuthIssue and unlinkGoogleDrive from deps - using refs now

  // Set up WebSocket notification subscriptions (connection is established on-demand during uploads)
  useEffect(() => {
    if (!user?.uid) return;

    const listenerCount = asyncUploadService['listeners']?.size || 0;
    logInfo('🔌 WebSocket: Setting up notification subscriptions', { 
      userId: user.uid, 
      trackId, 
      fileType: fileType || 'no-filter',
      existingListeners: listenerCount,
      timestamp: Date.now()
    });

    // Subscribe to all notification types (connection will be established automatically during uploads)
    const unsubscribeUpload = asyncUploadService.onUploadNotification(handleUploadNotification);
    const unsubscribeImport = asyncUploadService.onImportNotification(handleImportNotification);
    const unsubscribeAuth = asyncUploadService.onAuthIssue(handleAuthIssue);

    const newListenerCount = asyncUploadService['listeners']?.size || 0;
    logInfo('🔌 WebSocket: Subscriptions created', { 
      userId: user.uid, 
      trackId, 
      fileType: fileType || 'no-filter',
      totalListeners: newListenerCount
    });

    return () => {
      const beforeCleanupCount = asyncUploadService['listeners']?.size || 0;
      logInfo('🔌 WebSocket: Cleaning up notification subscriptions', { 
        userId: user.uid, 
        trackId, 
        fileType: fileType || 'no-filter',
        listenersBeforeCleanup: beforeCleanupCount
      });
      unsubscribeUpload();
      unsubscribeImport();
      unsubscribeAuth();
      const afterCleanupCount = asyncUploadService['listeners']?.size || 0;
      logInfo('🔌 WebSocket: Cleanup complete', { 
        userId: user.uid, 
        trackId, 
        fileType: fileType || 'no-filter',
        listenersAfterCleanup: afterCleanupCount
      });
      // Note: We don't disconnect here as the connection may be used by other components
      // The connection will timeout automatically after 5 minutes of inactivity
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, trackId, fileType, fileTypes]); // Handlers are stable via useCallback, don't include them

  return {
    // Expose methods that components can use if they need custom handling
    handleUploadNotification,
    handleImportNotification,
    handleAuthIssue
  };
};
